'use client';

import type { VariantProps } from 'class-variance-authority';
import type { FC } from 'react';
import { cva } from 'class-variance-authority';
import { useId, useState } from 'react';
import { cn } from '@/utils/cn';

const ratingVariants = cva(
  'inline-flex items-center',
  {
    variants: {
      size: {
        sm: 'size-3',
        md: 'size-4',
        lg: 'size-[18px]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

function StarIcon({
  filled,
  hovered,
  className,
}: {
  filled: boolean;
  hovered?: boolean;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={filled || hovered ? 0 : 1.5}
      className={cn(
        'transition-all text-c600 duration-150',
        filled && 'text-c300',
        className,
      )}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
      />
    </svg>
  );
}

interface GradientStarsProps {
  rating: number;
  hoverRating: number;
  maxRating: number;
  interactive?: boolean;
  onStarClick?: (index: number) => void;
  onStarHover?: (index: number) => void;
  onMouseLeave?: () => void;
  className?: string;
}

function GradientStars({
  rating,
  hoverRating,
  maxRating,
  interactive,
  onStarClick,
  onStarHover,
  onMouseLeave,
  className,
}: GradientStarsProps) {
  const uniqueId = useId();
  const gradientId = `star-gradient-${uniqueId}`;

  // coordinates for every star (ffrom Figma SVG)
  const starPaths = [
    'M22.4092 10.7188L34.2383 12.4375L25.6787 20.7812L27.6992 32.5625L17.1191 27L6.53906 32.5625L8.55957 20.7812L0 12.4375L11.8291 10.7188L17.1191 0L22.4092 10.7188Z',
    'M58.4092 10.7188L70.2383 12.4375L61.6787 20.7812L63.6992 32.5625L53.1191 27L42.5391 32.5625L44.5596 20.7812L36 12.4375L47.8291 10.7188L53.1191 0L58.4092 10.7188Z',
    'M94.4092 10.7188L106.238 12.4375L97.6787 20.7812L99.6992 32.5625L89.1191 27L78.5391 32.5625L80.5596 20.7812L72 12.4375L83.8291 10.7188L89.1191 0L94.4092 10.7188Z',
    'M130.409 10.7188L142.238 12.4375L133.679 20.7812L135.699 32.5625L125.119 27L114.539 32.5625L116.56 20.7812L108 12.4375L119.829 10.7188L125.119 0L130.409 10.7188Z',
    'M166.409 10.7188L178.238 12.4375L169.679 20.7812L171.699 32.5625L161.119 27L150.539 32.5625L152.56 20.7812L144 12.4375L155.829 10.7188L161.119 0L166.409 10.7188Z',
  ];

  const isHovering = interactive && hoverRating > 0;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 179 33"
      className={cn('transition-all duration-150', className)}
      onMouseLeave={onMouseLeave}
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="201.5"
          y1="36.8333"
          x2="17"
          y2="-4.66674"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.137716" stopColor="#F2DBFF" />
          <stop offset="1" stopColor="#FE5104" />
        </linearGradient>
      </defs>
      {starPaths.slice(0, maxRating).map((path, index) => {
        const isFilled = !isHovering && index < rating;
        const isHovered = isHovering && index < hoverRating;
        const shouldFill = isFilled || isHovered;

        return (
          <path
            key={index}
            d={path}
            fill={shouldFill ? `url(#${gradientId})` : 'none'}
            stroke="currentColor"
            strokeWidth={shouldFill ? 0 : 1}
            className={cn(
              'transition-all duration-150',
              interactive && 'cursor-pointer',
            )}
            style={{
              color: shouldFill ? 'transparent' : '#94A3B8',
            }}
            onClick={() => interactive && onStarClick?.(index)}
            onMouseEnter={() => interactive && onStarHover?.(index)}
          />
        );
      })}
    </svg>
  );
}

interface RatingStarsProps extends VariantProps<typeof ratingVariants> {
  className?: string;
  rating: number;
  maxRating?: number;
  showValue?: boolean;
  interactive?: boolean;
  onChange?: (rating: number) => void;
  variant?: 'default' | 'gradient';
}

export const RatingStars: FC<RatingStarsProps> = ({
  className,
  size,
  interactive,
  onChange,
  rating: initialRating = 0,
  maxRating = 5,
  showValue = true,
  variant = 'default',
}) => {
  const [rating, setRating] = useState(initialRating);
  const [hoverRating, setHoverRating] = useState(0);

  const handleClick = (index: number) => {
    if (!interactive) {
      return;
    }
    const newRating = index + 1;
    setRating(newRating);
    onChange?.(newRating);
  };

  const handleMouseEnter = (index: number) => {
    if (!interactive) {
      return;
    }
    setHoverRating(index + 1);
  };

  const handleMouseLeave = () => {
    if (!interactive) {
      return;
    }
    setHoverRating(0);
  };

  const displayRating = hoverRating || rating;

  if (variant === 'gradient') {
    return (
      <div
        className={cn(
          'flex items-center',
          size === 'md' && 'gap-1.5 h-7',
          size === 'sm' && 'h-4 gap-1',
          size === 'lg' && 'gap-2 h-8',
          className,
        )}
      >
        <GradientStars
          rating={rating}
          hoverRating={hoverRating}
          maxRating={maxRating}
          interactive={interactive}
          onStarClick={handleClick}
          onStarHover={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={cn(
            size === 'sm' && 'h-4',
            size === 'md' && 'h-7',
            size === 'lg' && 'h-8',
          )}
        />
        {showValue && displayRating !== 0 && (
          <span
            className={cn(
              'font-semibold text-light tracking-normal',
              size === 'md' && 'text-lg leading-7',
              size === 'sm' && 'text-xs leading-4',
              size === 'lg' && 'text-xl leading-8',
            )}
          >
            {displayRating}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center',
        size === 'md' && 'gap-1.5 h-7',
        size === 'sm' && 'h-4 gap-1',
        className,
      )}
    >
      <div
        className={cn('flex items-center', size === 'md' && 'gap-0.75', size === 'sm' && 'gap-0.5')}
        onMouseLeave={handleMouseLeave}
      >
        {Array.from({ length: maxRating }).map((_, index) => {
          const isHovering = interactive && hoverRating > 0;
          const isFilled = !isHovering && index < rating;
          const isHovered = isHovering && index < hoverRating;

          return (
            <button
              type="button"
              key={index}
              onClick={() => handleClick(index)}
              onMouseEnter={() => handleMouseEnter(index)}
              className={cn(
                'flex justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B7ED9] focus-visible:ring-offset-1 focus-visible:ring-offset-[#3D2A5C]',
                interactive && 'cursor-pointer hover:scale-110 transition-transform',
              )}
              disabled={!interactive}
              aria-label={`Rate ${index + 1} out of ${maxRating}`}
            >
              <StarIcon
                filled={isFilled}
                hovered={isHovered}
                className={cn(ratingVariants({ size }))}
              />
            </button>
          );
        })}
      </div>
      {showValue && displayRating !== 0 && (
        <span className={cn('font-semibold text-light tracking-normal', size === 'lg' && 'ml-2', size === 'md' && 'text-lg leading-7', size === 'sm' && 'text-xs leading-4')}>
          {displayRating}
        </span>
      )}
    </div>
  );
};
